import React from "react";
import clsx from "clsx";
import { useTranslation } from "lib/i18n/use-translation";
import { Button } from "components/ui";
import { logAnalyticsEvent } from "utility/FirebaseAnalytics";
import { trackEvent } from "utility/mixpanel";
import { ReactComponent as LockIcon } from "Assets/icon/icon-lock.svg";
import { useEffect, useCallback } from "react";
import { useInViews } from "utility/useInViews";
import { handleRetakeStartAssessment } from "../../pages/woneindex/utils";
import { AssessmentSchedule } from "../../api/types";
import useResumeChoice from "../../hooks/use-resume-choice";

import styles from "./stat-cards/stat-cards.module.scss";
import { useLocation } from "react-router-dom";

interface AssessmentItemCardProps {
  link?: string;
  assessmentSchedule?: AssessmentSchedule | null;
}

const AssessmentItemCard = ({
  link,
  assessmentSchedule = null,
}: AssessmentItemCardProps) => {
  const { ref, inView } = useInViews();
  const location = useLocation();
  const { t } = useTranslation(["components", "index", "home"]);
  const { requestResume, resumeChoiceModal } = useResumeChoice();

  const isFirstAssessmentTaken = Boolean(
    assessmentSchedule?.previousAssessmentTakenAt
  );

  // Saved progress past its expiry window already reads as false server-side,
  // so a stale attempt never surfaces the resume prompt.
  const hasResumableProgress = Boolean(
    assessmentSchedule?.hasResumableProgress
  );

  const handleAssessmentButtonClick = async (
    e: React.MouseEvent<HTMLElement>
  ) => {
    if (e.target instanceof HTMLButtonElement) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!link) {
      return;
    }

    trackEvent("Take Assessment Clicked", {
      "Button Label": hasResumableProgress
        ? "Resume my assessment"
        : "Take my assessment",
      "Has Resumable Progress": hasResumableProgress,
      "Previous Assessment Response ID":
        assessmentSchedule?.previousAssessmentResponseId || null,
      "Previous Assessment Taken At":
        assessmentSchedule?.previousAssessmentTakenAt || null,
      "Next Assessment Due At": assessmentSchedule?.nextAssessmentDueAt || null,
      "Is Next Assessment Due":
        assessmentSchedule?.isNextAssessmentDue || false,
      "Days Until Next Assessment Due":
        assessmentSchedule?.daysUntilNextAssessmentDue || null,
      "Current Domain": window.location.origin,
      Destination: location.pathname,
      indexVersion: "v3",
      "Assessment Type": isFirstAssessmentTaken ? "retake" : "first",
    });
    await logAnalyticsEvent("take_assessment_clicked", {});

    let fullUrl: URL;
    if (link.startsWith("http://") || link.startsWith("https://")) {
      fullUrl = new URL(link);
    } else {
      fullUrl = new URL(link, window.location.origin);
    }

    requestResume(hasResumableProgress, () =>
      handleRetakeStartAssessment(e, fullUrl)
    );
  };

  const logImpression = useCallback(() => {
    logAnalyticsEvent("take_assessment_card_impression", {});

    trackEvent("Take Assessment Card Impression", {
      "Previous Assessment Response ID":
        assessmentSchedule?.previousAssessmentResponseId || null,
      "Previous Assessment Taken At":
        assessmentSchedule?.previousAssessmentTakenAt || null,
      "Next Assessment Due At": assessmentSchedule?.nextAssessmentDueAt || null,
      "Is Next Assessment Due":
        assessmentSchedule?.isNextAssessmentDue || false,
      "Days Until Next Assessment Due":
        assessmentSchedule?.daysUntilNextAssessmentDue || null,
      "Current Domain": window.location.origin,
      Destination: location.pathname,
      indexVersion: "v3",
      "Assessment Type": isFirstAssessmentTaken ? "retake" : "first",
    });
  }, [
    assessmentSchedule?.daysUntilNextAssessmentDue,
    assessmentSchedule?.isNextAssessmentDue,
    assessmentSchedule?.nextAssessmentDueAt,
    assessmentSchedule?.previousAssessmentResponseId,
    assessmentSchedule?.previousAssessmentTakenAt,
    isFirstAssessmentTaken,
    location.pathname,
  ]);

  useEffect(() => {
    if (inView) {
      logImpression();
    }
  }, [inView, logImpression]);

  return (
    <div className={styles["assessment-item-card"]} ref={ref}>
      {resumeChoiceModal}
      {/*
        Illustrative scores, not the learner's own — hidden from assistive tech
        so a locked-state decoration is never read out as a real score. Every
        state in the designs carries this, first-time and repeat alike.
      */}
      <div
        className={styles["assessment-card-score-preview"]}
        aria-hidden="true"
      >
        <span
          className={clsx(
            styles["assessment-card-score"],
            styles["assessment-card-score--far"]
          )}
        >
          77
        </span>
        <span
          className={clsx(
            styles["assessment-card-score"],
            styles["assessment-card-score--near"]
          )}
        >
          78
        </span>
        <span className={styles["assessment-card-lock"]}>
          <LockIcon />
        </span>
        <span
          className={clsx(
            styles["assessment-card-score"],
            styles["assessment-card-score--near"]
          )}
        >
          80
        </span>
        <span
          className={clsx(
            styles["assessment-card-score"],
            styles["assessment-card-score--far"]
          )}
        >
          81
        </span>
      </div>
      <p
        className={clsx(styles["assessment-card-text"], {
          [styles["assessment-card-text--resumable"]]: hasResumableProgress,
          [styles["assessment-card-text--repeat"]]:
            isFirstAssessmentTaken && !hasResumableProgress,
        })}
      >
        {hasResumableProgress
          ? t("index:trackPage.resumeStateHeader")
          : isFirstAssessmentTaken
          ? t("components:assessmentPromptCard.title")
          : t("components:indexAssessment.title")}
      </p>
      <div className={styles["assessment-card-content"]}>
        <p
          className={clsx(styles["assessment-card-description"], {
            [styles["assessment-card-description--resumable"]]:
              hasResumableProgress,
          })}
        >
          {hasResumableProgress
            ? t("index:trackPage.resumeStateSubtitle")
            : isFirstAssessmentTaken
            ? t("components:assessmentPromptCard.body")
            : t("components:indexAssessment.body")}
        </p>
      </div>
      <div className={styles["index-score-button-container"]}>
        <Button
          className={clsx(
            styles["assessment-card-button"],
            styles["index-score-button"]
          )}
          type="button"
          variant="enclosed-primary"
          monochromeColour="white"
          onClick={handleAssessmentButtonClick}
        >
          {hasResumableProgress
            ? t("index:trackPage.resumeAssessment")
            : t("components:indexAssessment.cta")}
        </Button>
      </div>
    </div>
  );
};

export default AssessmentItemCard;
